import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faSpinner } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { showToast, handleAPIError } from '@/utils/toasts';
import Modal from '@/components/ui/Modal';
import Checkbox from '@/components/ui/Checkbox';
import type { Subscription, UserID } from '@/models';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  subscription: Subscription;
  userId: UserID;
  onCanceled: (subscription: Subscription) => void;
}

export default function CancelSubscriptionModal({
  isOpen,
  onClose,
  subscription,
  userId,
  onCanceled
}: CancelSubscriptionModalProps) {
  const [cancelImmediately, setCancelImmediately] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCancel = async () => {
    setIsLoading(true);
    try {
      const response = await APIRoutes.cancelSubscription(userId, subscription.id, {
        cancelImmediately
      });
      showToast('Abonnement erfolgreich gekündigt', 'success');
      onCanceled(response.subscription);
      onClose();
    } catch (error) {
      handleAPIError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setCancelImmediately(false);
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Abonnement kündigen"
      icon={<FontAwesomeIcon icon={faExclamationTriangle} className="w-5 h-5 text-red-600" />}
      maxWidth="md"
      closeDisabled={isLoading}
    >
      <div className="p-6">
        <div className="mb-6">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            Möchten Sie das Abonnement wirklich kündigen?
          </p>
          <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">Status:</span>{' '}
              {subscription.status}
            </p>
            {subscription.currentPeriodEnd !== null && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                <span className="font-medium text-gray-900 dark:text-white">Aktuelle Periode endet:</span>{' '}
                {new Date(subscription.currentPeriodEnd).toLocaleDateString('de-DE')}
              </p>
            )}
          </div>
        </div>

        <div className="mb-6">
          <Checkbox
            checked={cancelImmediately}
            onChange={setCancelImmediately}
            disabled={isLoading}
            colorTheme="red"
            label="Sofort kündigen (ohne Restlaufzeit)"
            description={cancelImmediately
              ? 'Das Abonnement wird sofort beendet und der Zugang wird umgehend entzogen.'
              : 'Das Abonnement wird zum Ende der aktuellen Periode gekündigt. Der Zugang bleibt bis dahin erhalten.'}
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {isLoading && <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />}
            Kündigen
          </button>
        </div>
      </div>
    </Modal>
  );
}
